"use client";

import { useEffect, useState, useRef } from "react";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { fontWeights } from "@/utils/fonts";
import { toast } from "sonner";
import IntlPhoneInput, { PhoneInputRef, PhoneData } from "../ui/PhoneInput";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

export function ContactScrollPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const pathname = usePathname();
  const phoneInputRef = useRef<PhoneInputRef>(null);

  const formFields = {
    name: "",
    email: "",
    contactNumber: "",
    countryCode: "",
    message: "",
  };

  const [formData, setFormData] = useState(formFields);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  // Reset state on navigation so the popup can trigger again on the new page
  useEffect(() => {
    setIsDismissed(false);
    setFormData(formFields);
    setFormErrors({});

    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (params.get("contact") === "true") {
        setIsVisible(true);
        // Clean up the URL state without reloading
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      } else {
        setIsVisible(false);
      }
    } else {
      setIsVisible(false);
    }
  }, [pathname]);

  useEffect(() => {
    const handleOpen = () => {
      setIsVisible(true);
      setIsDismissed(false);
    };

    window.addEventListener("openContactPopup", handleOpen);
    return () => window.removeEventListener("openContactPopup", handleOpen);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (isDismissed || isVisible) return;

      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      const clientHeight = document.documentElement.clientHeight;

      const scrollPercentage = ((scrollTop + clientHeight) / scrollHeight) * 100;

      if (scrollPercentage >= 80) {
        setIsVisible(true);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isDismissed, isVisible]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));

    if (formErrors[name]) {
      setFormErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handlePhoneChange = (phoneData: PhoneData) => {
    setFormData(prev => ({
      ...prev,
      contactNumber: phoneData.contactNumber,
      countryCode: phoneData.countryCode,
    }));
    if (phoneData.isValid) {
      setFormErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors.contactNumber;
        return newErrors;
      });
    }
  };

  const submitHandler = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errors: { [key: string]: string } = {};
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!formData.name) errors.name = "Name is required.";
    else if (formData.name.length < 2 || formData.name.length > 15) {
      errors.name = "Name must be between 2 and 15 characters.";
    }

    if (!formData.email) errors.email = "Email is required.";
    else if (!emailRegex.test(formData.email)) errors.email = "Invalid email format.";

    let currentContactNumber = formData.contactNumber;
    let currentCountryCode = formData.countryCode;
    let isValid = true;

    if (!currentContactNumber && phoneInputRef.current) {
      const phoneData = phoneInputRef.current.getPhoneData();
      currentContactNumber = phoneData.contactNumber;
      currentCountryCode = phoneData.countryCode;
      isValid = phoneData.isValid;
    } else if (phoneInputRef.current) {
      isValid = phoneInputRef.current.getPhoneData().isValid;
    }

    if (!currentContactNumber) {
      errors.contactNumber = "Please enter contact number";
    } else if (!currentCountryCode) {
      errors.contactNumber = "Please select the country";
    } else if (!isValid) {
      errors.contactNumber = "Please enter a valid contact number";
    }

    if (!formData.message) errors.message = "Message is required.";

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        contactNumber: `+${currentCountryCode}${currentContactNumber}`,
        message: formData.message,
      };
      const response = await fetch("https://api.bitsshadow.com", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        setFormData(formFields);
        phoneInputRef.current?.reset();
        toast.success("Your inquiry is successfully submitted!", {
          position: "top-right",
          className: "bg-green-100 text-green-800 border border-green-400",
        });
        setIsVisible(false);
        setIsDismissed(true);
      } else {
        throw new Error("Failed to submit form");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to submit form. Please try again later.");
    }
  };

  return (
    <AnimatePresence>
      {isVisible && !isDismissed && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />

          {/* Modal Content - Copied UI of ContactUsForm */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="relative z-10 w-full max-w-[450px]"
          >
            <div className="bg-[#002F6D] text-white rounded-[10px] flex flex-col items-center mx-4 h-auto p-4 tsm-350:p-5 sm:p-7 lg:p-8 relative overflow-visible">
              <button
                onClick={() => setIsDismissed(true)}
                className="absolute top-4 right-4 text-white/70 group hover:bg-white transition-all rounded-full p-[3px] sm:p-1 border border-white"
              >
                <X className="text-white group-hover:text-[#002F6D] w-[16px] sm:w-[20px] h-[16px] sm:h-[20px] transition-colors" />
              </button>

              <h2
                className="flex items-center
                text-[14px]
                tsm-350:text-[16px]
                tsm-400:text-[18px]
                sm:text-[20px]
                md:text-[22px]
                lg:text-[24px]
                mb-[20px]
                tsm-350:mb-[18px]
                tsm-400:mb-[25px]
                sm:mb-[25px]
                md:mb-[28px]
                lg:mb-[28px]
                xl:mb-[35px]
                2xl:mb-[35px]
                "
                style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.medium, lineHeight: "1.2" }}
              >
                Contact Us <span className="ml-1 sm:ml-[6px] md:ml-[7px] lg:ml-2">👋</span>
              </h2>

              <form
                onSubmit={submitHandler}
                className="w-full justify-center text-[10px] tsm-350:text-[12px] tsm-400:text-[14px] sm:text-[15px] md:text-[16px] lg:text-[17px] xl:text-[20px]"
                style={{ fontFamily: "Asap", fontWeight: fontWeights.lato.regular, lineHeight: "1.2" }}
              >
                <div className="flex flex-col gap-4 max-w-[650px] mx-auto">
                  <div className="flex flex-col gap-2">
                    <Input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleChange}
                    />
                    {formErrors.name && (
                      <p className="text-red-500 text-[10px] tsm-350:text-[12px] mt-[2px]">{formErrors.name}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <Input
                      type="email"
                      name="email"
                      placeholder="Enter Official Email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                    {formErrors.email && (
                      <p className="text-red-500 text-[10px] tsm-350:text-[12px] mt-[2px]">{formErrors.email}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 relative z-[100]">
                    <IntlPhoneInput
                      onChange={handlePhoneChange}
                      name="contactNumber"
                      ref={phoneInputRef}
                    />
                    {formErrors.contactNumber && (
                      <p className="text-red-500 text-[10px] tsm-350:text-[12px] mt-[2px]">{formErrors.contactNumber}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <Textarea
                      name="message"
                      placeholder="Your Message"
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                    />
                    {formErrors.message && (
                      <p className="text-red-500 text-[10px] tsm-350:text-[12px] mt-[2px]">{formErrors.message}</p>
                    )}
                  </div>
                </div>
                <button
                  type="submit"
                  className="bg-white text-[#002F6D] mx-auto block rounded-[5px] text-center
                    px-[8px]
                    tsm-350:px-3
                    md:px-[14px]
                    lg:px-4 py-1
                    tsm-350:py-[6px]
                    md:py-[7px]
                    lg:py-2
                    mt-4
                    md:mt-[20px]
                    lg:mt-[28px]
                    text-[14px]
                    sm:text-[16px]
                "
                  style={{ fontFamily: "Asap", fontWeight: fontWeights.lato.medium, lineHeight: "1.2" }}
                >
                  Send Message
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
